import { Button, CloseButton } from 'components/ui'
import React from 'react'
import { FormContext } from 'react-hook-form'
import { Plus, Folder } from 'lucide-react'
import { Content, Subtask, useFormStepContext } from 'components/TasksV2/TaskCreation/FormStepContext'
import { SubtaskBuilder } from 'components/TasksV2/TaskCreation/components/SubtaskBuilder'
import { useSelector } from 'react-redux'
import { isRtlSelector } from 'redux/selectors/general'
import { CategoryGroup } from '../CategoryGroup'
import { useCreationText } from 'components/TasksV2/TaskCreation/hooks/useCreationText'
import { AI_IMAGE_BUTTON_TYPE } from 'components/TasksV2/TaskCreation/hooks/useSubtaskMenu'
import { IContentFormData } from '../../types'
import { CategoryPicker } from '../CategoryPicker'
import styles from './styles.module.css'
import { useCategoryContent } from '../../hooks/useCategoryContent'
import ConfirmationPopup from 'components/CommonComponents/ConfirmationPopup'
import useLocaleText from 'components/useLocaleText'
import { useCategoryContentText } from './strings'

interface CategoryContentStepProps {
  onNext: (data: IContentFormData) => void
  headerSlot?: HTMLDivElement | null
  initialData?: IContentFormData
}

/** Adapts category draft/group ownership to the same RHF widgets and local AI editor as flat creation. */
export function CategoryContentStep({ onNext, initialData, headerSlot }: CategoryContentStepProps) {
  const { isEdit } = useFormStepContext()
  const {
    methods,
    fields,
    busy,
    values,
    categories,
    active,
    activeCategory,
    setActiveCategory,
    choosingCategory,
    setChoosingCategory,
    emptyCategoryNames,
    showRestoredDraft,
    setShowRestoredDraft,
    addCategories,
    reorderSubtasks,
    pendingCategory,
    setPendingCategory,
    removeCategory,
  } = useCategoryContent(onNext, initialData)
  const confirmTitle = useLocaleText('card_confirm_text')
  const confirmDescription = useLocaleText('card_confirm_desc')
  const yes = useLocaleText('yes')
  const no = useLocaleText('no')
  const t = useCategoryContentText()
  const creationText = useCreationText()
  const rtl = useSelector(isRtlSelector)

  const validationMessage = !categories.length
    ? t.needCategory
    : emptyCategoryNames.length
    ? `${t.needSubtask}: ${emptyCategoryNames.join(', ')}`
    : undefined

  return (
    <FormContext {...methods}>
      <SubtaskBuilder
        headerSlot={headerSlot}
        fields={fields as (Subtask | Content)[]}
        categoryGrouping={{
          previewGroups: categories.map((category) => ({
            name: category.name,
            subtasks: (values.sub_tasks || [])
              .filter((item: Subtask) => item.category === category.name)
              .map((item: Subtask) => ({
                title: item.title || '',
                type: item.is_ai_enhanced ? AI_IMAGE_BUTTON_TYPE : item.type,
              })),
          })),
          intro: (taskName) => (
            <div className={styles.intro}>
              <h2>{creationText.buildTask}</h2>
              <div className={styles.name}>
                <span aria-hidden="true">1</span>
                {taskName}
              </div>
              <h3>
                <span aria-hidden="true">2</span>
                {t.chooseCategory}
              </h3>
              <p>{t.categoryHint}</p>
              <CategoryPicker
                existingNames={[]}
                onConfirm={addCategories}
                disabled={!!busy || !values.title?.trim() || values.title.length > 100}
              />
              <div className={styles.nextStep}>
                <span>3</span>
                {t.addSubtasks}
              </div>
            </div>
          ),
          activeCategory: active?.name,
          onReorder: reorderSubtasks,
          dockHint: active ? (
            <span className={styles.addingTo}>
              <Folder size={15} />
              <span>{t.addingTo}</span>
              <strong dir="auto">{active.name}</strong>
            </span>
          ) : undefined,
          canAddSubtasks: !!active,
          validationMessage,
          onAddSubtask: () => setChoosingCategory(false),
          onSelectSubtask: (index) => {
            const categoryName = (values.sub_tasks?.[index] || fields[index])?.category
            const category = categories.find((item) => item.name === categoryName)
            if (category) setActiveCategory(category.id)
            setChoosingCategory(false)
          },
          editor:
            choosingCategory && categories.length ? (
              <CategoryPicker
                existingNames={categories.map((category) => category.name)}
                onConfirm={addCategories}
                disabled={!!busy}
                onCancel={() => setChoosingCategory(false)}
              />
            ) : undefined,
          controls: (
            <div className={styles.categories} hidden={!categories.length}>
              {showRestoredDraft && (
                <div className={styles.restored} role="status">
                  <span>{t.restored}</span>
                  <CloseButton
                    type="button"
                    height="28px"
                    minWidth="28px"
                    aria-label={t.dismiss}
                    onClick={() => setShowRestoredDraft(false)}
                  />
                </div>
              )}
              {validationMessage && <p className={styles.validation}>{validationMessage}</p>}
              <div className={styles.heading}>
                <strong>
                  {t.categories} <span>{categories.length}</span>
                </strong>
                {!isEdit && (
                  <Button
                    variant="secondary"
                    height="auto"
                    type="button"
                    isDisabled={!!busy}
                    onClick={() => setChoosingCategory(true)}
                  >
                    <Plus size={16} /> {t.addCategory}
                  </Button>
                )}
              </div>
            </div>
          ),
          renderItems: (renderItem, actions) => (
            <>
              {fields
                .flatMap((_, index) => (!(values.sub_tasks?.[index] || fields[index])?.category ? [index] : []))
                .map((index, dragIndex) => renderItem(index, dragIndex))}
              {categories
                .filter((category) => !!category.name)
                .map((category) => (
                  <CategoryGroup
                    key={category.id}
                    id={category.id}
                    name={category.name}
                    active={activeCategory === category.id}
                    readOnly={isEdit}
                    busy={!!busy}
                    rtl={rtl}
                    buttons={actions.buttons}
                    renderItem={renderItem}
                    indices={fields.flatMap((_, index) =>
                      (values.sub_tasks?.[index] || fields[index])?.category === category.name ? [index] : []
                    )}
                    onActivate={() => {
                      setActiveCategory(category.id)
                      setChoosingCategory(false)
                      actions.clearEditor()
                    }}
                    onAdd={actions.onAdd}
                    onRemove={() => setPendingCategory(category.id)}
                    removeLabel={`${t.deleteCategory} ${category.name}`}
                  />
                ))}
            </>
          ),
        }}
      />
      <ConfirmationPopup
        isOpen={!!pendingCategory}
        onConfirm={() => {
          if (pendingCategory) removeCategory(pendingCategory)
        }}
        onCancel={() => setPendingCategory(null)}
        title={confirmTitle}
        description={confirmDescription}
        confirmButtonText={yes}
        cancelButtonText={no}
        isLoading={!!busy}
      />
    </FormContext>
  )
}
